import React from 'react';
import { observer } from 'mobx-react';
import { SvgIcon, Box } from 'components';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import color from 'styles/themes/wikifactory/styles/colors';
import { FeatureStyled } from './feature.styled';

interface FeatureProps {
  feature: FeatureObjProps;
  ix: number;
  icon?: React.ReactNode;
}

interface Vec2Props {
  x: number;
  y: number;
}

interface FeatureObjProps {
  // wasm [MeshFeature]
  selfIx: number;
  span: number;
  volume: number;
  curveType: Object; // wasm enum CurveType
  isThrough: boolean;
  GetExtent(): Vec2Props;
}

export const Feature = observer(({ icon, feature, ix }: FeatureProps) => {
  if (!feature) return null;

  const viewerControl = useViewerControl();
  const selManager = viewerControl?.viewer?.selectionManager;
  if (!selManager) return null;
  const selected = selManager.IsModelItemSelectionTarget(feature);
  const highlighted = selManager.IsModelItemHighlightTarget(feature);

  const curveEnum = viewerControl?.viewer?.module.CurveType;
  const { span, curveType, isThrough } = feature;

  // const fIndex = `${feature.selfIx + 1}.`; // debug
  const fIndex = `${ix + 1}.`;
  const fSpan = `${span.toFixed(1)}`;

  const extent = feature.GetExtent();
  const fSize =
    extent.y < 0
      ? `${extent.x.toFixed(1)}` // diameter
      : `[${extent.x.toFixed(1)} , ${extent.y.toFixed(1)}]`; // rectangle box

  const spanSymbol = !isThrough ? '\u21A7' : '\u2195'; // depth (↧, ↕)
  // relevant size is extent...
  let sizeSymbol = '';
  if (curveType === curveEnum.CurveTypeCircle) sizeSymbol = '\u2300';
  // (⌀)
  else if (curveType === curveEnum.CurveTypeEllipse) sizeSymbol = '\u2B2D';
  // (⬭)
  else sizeSymbol = '\u2B21'; // undefined/polygon (⬡)

  /*
    render
  */

  return (
    <FeatureStyled
      selected={selected}
      highlighted={highlighted}
      onMouseEnter={() => selManager.AddHighlightItem(feature)}
      onMouseLeave={() => selManager.ClearHighlight()}
    >
      {icon && <SvgIcon icon={icon} size='medium' />}
      <Box
        mr={4}
        sx={{ width: '20px', textAlign: 'right', color: color.wfGrey700 }}
      >
        {fIndex}
      </Box>
      <Box ml={1} sx={{ width: '110px', color: color.wfBlack }}>
        {`${sizeSymbol} ${fSize}`}
      </Box>
      <Box ml={1} sx={{ width: '50px', color: color.wfBlack }}>
        {`${spanSymbol} ${fSpan}`}
      </Box>
    </FeatureStyled>
  );
});
